/* eslint-disable @typescript-eslint/naming-convention */
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, OnInit, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  switchMap,
  take,
  takeUntil,
  withLatestFrom
} from 'rxjs/operators';

import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { claimDashboardQuery, DashboardClaim } from '@amfam/claim/dashboard/data-access';
import { env } from '@amfam/environment';
// Store
import * as fromOpportunity from '@amfam/opportunities';
import { CoreService } from '@amfam/opportunities';
import {
  policiesQuery,
  PolicyDocumentService,
  PolicySelectors,
  PolicySummarySelectors
} from '@amfam/policy/data-access';
import { GoPaperLessSelectors } from '@amfam/policy/go-paperless/data-access';
import { AnalyticsActions, AnalyticsFacade, PageAnalyticParams } from '@amfam/shared/analytics';
import { OverviewAnalytics } from '@amfam/shared/models';
import { DocumentPipeService } from '@amfam/shared/ui/pipes';
import { AccountTypeEnum, userQuery } from '@amfam/shared/user';
import { Agent } from '@amfam/shared/utility/agent';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import {
  CookiesService,
  CopyService,
  OpportunityContentWithRecommendationId,
  WindowRef
} from '@amfam/shared/utility/shared-services';
import {
  BannerConfig,
  DockingBarService,
  LoadingSpinnerService,
  PageTitleService,
  ToasterService
} from '@amfam/ui-kit';

import * as fromRoot from '../../core/store';
import { ForgotPasswordAnalytics } from '../../login/forgot-password/shared/forgot-password-analytic-constants';
import { OverviewService } from './overview.service';

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  standalone: false
})
export class OverviewComponent implements OnInit, AfterViewInit {
  public static readonly myAccountText = 'My Account';
  public isEnrolled: boolean;
  public overviewContent: string;
  public overviewVehicleTitle: string;
  public vehicleName: string;
  public opportunitiesEnabled$: Observable<boolean>;
  public showAlert = false;
  public agents$: Observable<Agent[]>;
  public customerFirstName: string;
  disclaimers$: Observable<string[]>;
  policyNumber: string;
  documentLink: string;
  documentDate: string;

  public alertTitle = this.copyService.getCopy('overview.kyd.alertTitle');
  public alertMessage = this.copyService.getCopy('overview.kyd.alertMessage');
  public title: string;
  public dismissBanner;
  public bannerForPartner: string;
  public showBanner = false;
  duplicateEmailBannerConfig: BannerConfig;
  programsLoading: Observable<boolean>;
  hasAutoDiscountsToEnroll: Observable<boolean>;
  public deepLinkingNoDocuments = false;
  // recently opened claims to be retrieved from context api
  dashboardClaims$: Observable<DashboardClaim[]>;
  claimsLoading$: Observable<boolean>;
  public overviewOpportunitiesContent$: Observable<OpportunityContentWithRecommendationId[]>;
  isDuplicateEmail = false;
  autoPolicies$: Observable<any>;
  kydMigrationBanner = false;
  showTestOnlyGetQuoteButton = this.featureFlagService.isEnabled('testonly_quote_dsp');
  isInt01 = env.environment === 'int01';
  private stop$: Subject<void> = new Subject<void>();
  private isDocumentCall = false;

  constructor(
    private dockingService: DockingBarService,
    private pageTitle: PageTitleService,
    private store: Store<fromRoot.RootState>,
    private toaster: ToasterService,
    private copyService: CopyService,
    private cookiesService: CookiesService,
    private featureFlagService: FeatureFlagService,
    private overviewService: OverviewService,
    private policyDocumentService: PolicyDocumentService,
    private documentPipeService: DocumentPipeService,
    private win: WindowRef,
    private sanitizer: DomSanitizer,
    private analyticsFacade: AnalyticsFacade,
    private windowRef: WindowRef,
    private router: Router,
    private coreService: CoreService,
    private spinner: LoadingSpinnerService
  ) {}

  ngOnInit() {
    localStorage.removeItem('frFlowType');
    sessionStorage.removeItem(this.copyService.getCopy('auth.pendingRedirectKey'));
    if (localStorage.getItem('vehicleName')) {
      this.showAlert = true;
    } else {
      this.showAlert = false;
    }
    if (this.cookiesService.hasItem('isDuplicateEmail')) {
      this.isDuplicateEmail = true;
      this.analyticsFacade.trackPage(OverviewAnalytics.overviewMultipleEmailErrorAnalytic);
    }
    this.store.pipe(select(routerQuery.getRouterPreviousStateUrl), take(1)).subscribe(url => {
      if (url && url.includes('newdocumentlink')) {
        this.deepLinkingNoDocuments = true;
      }
    });
    this.overviewVehicleTitle = this.copyService.getCopy('overview.vehicleOverviewtitle', {
      vehicleName: localStorage.getItem('vehicleName')
    });
    this.bannerForPartner = this.copyService.getCopy('overview.partnerBanner');
    this.overviewContent = this.copyService.getCopy('overview.vehicleOverviewContent');
    this.autoPolicies$ = this.store.select(PolicySelectors.selectVehiclePolicies);
    this.programsLoading = this.store.select(fromRoot.programsLoading);
    this.hasAutoDiscountsToEnroll = this.store.select(fromRoot.hasAutoDiscountsToEnroll);

    this.dockingService.registerHeading(OverviewComponent.myAccountText);
    this.pageTitle.set(OverviewComponent.myAccountText);

    this.opportunitiesEnabled$ = this.store.pipe(select(fromRoot.opportunitiesEnabled));
    this.agents$ = this.store.pipe(select(fromRoot.getAllActiveAgents));

    this.dashboardClaims$ = this.store.pipe(
      select(claimDashboardQuery.selectDisplayedDashboardClaims)
    );
    this.claimsLoading$ = this.store.pipe(select(claimDashboardQuery.selectLoading));

    this.store.pipe(select(userQuery.selectFirstName), take(1)).subscribe(firstName => {
      this.customerFirstName = firstName;
      this.spinner.stop();
    });

    // Build the overview content
    this.overviewOpportunitiesContent$ = this.store.pipe(
      select(fromOpportunity.selectOpportunitiesNoDismissed),
      filter(opportunityContent => !!opportunityContent.length),
      take(1)
    );

    this.disclaimers$ = this.overviewOpportunitiesContent$.pipe(
      map(content => this.overviewService.getDisclaimers(content))
    );

    // AS: Handle the error and success when coming from the gopaperless screen.
    this.store
      .pipe(
        select(routerQuery.getRouterPreviousStateUrl),
        filter(url => !!url && url.includes('program/gopaperless')),
        withLatestFrom(
          this.store.select(GoPaperLessSelectors.selectGoPaperlessError),
          this.store.select(BillAccountsSelectors.selectBillAccountsWithUpdatePreferenceFailure),
          this.store.select(fromRoot.getPrograms)
        ),
        take(1)
      )
      .subscribe(([url, goPaperlessError, billAccountsWithPrefError, programs]) => {
        // If we are coming from the go paperless screen and have a go paperless error we need to display the error
        // otherwise we need to display the account created toaster.
        if (
          !goPaperlessError &&
          billAccountsWithPrefError.length === 0 &&
          !(_get(programs, 'length', 0) > 0)
        ) {
          this.toaster.pop('success', this.copyService.getCopy('goPaperless.goPaperLessSuccess'));
        }
      });
    if (this.isDuplicateEmail) {
      this.duplicateEmailBannerConfig = {
        headingTextPath: 'overview.duplicateEmailText',
        buttonText: this.copyService.getCopy('overview.reliefCredit.bannerButtonText')
      };
    }
    // this code is added to handle the kydbanner based on the reasoncode, usually we dont' need to know the reasoncode for eligiblity call
    this.store
      .select(fromRoot.getKydPolicyEligibility)
      .pipe(takeUntil(this.stop$))
      .subscribe(policyEligibilities => {
        policyEligibilities.forEach(policyEligibility => {
          if (policyEligibility.reasonCode === '400007') {
            this.kydMigrationBanner = true;
            if (!this.isDocumentCall) {
              this.getPolicyDocument();
            }
          }
        });
      });

    combineLatest(
      this.store.select(PolicySummarySelectors.selectAllPolicySummaries),
      this.store.select(PolicySummarySelectors.selectHomesitePolicies),
      this.store.select(PolicySummarySelectors.selectPolicySummaryLoading),
      (allPoliciesSummary, homesitePolicies, summariesLoading) => ({
        allPoliciesSummary: allPoliciesSummary,
        homesitePolicies: homesitePolicies,
        summariesLoading: summariesLoading
      })
    )
      .pipe(
        takeUntil(this.stop$),
        filter(state => !state.summariesLoading)
      )

      .subscribe(state => {
        if (state.allPoliciesSummary.length > 0) {
          state.allPoliciesSummary.forEach(policySummary => {
            if (
              policySummary.policyTypeCode === 'HO3' ||
              policySummary.policyTypeCode === 'HO4' ||
              policySummary.policyTypeCode === 'HO6' ||
              policySummary.policyTypeCode === 'HF9'
            ) {
              const effectiveDate = formatDate(policySummary.effectiveDate, 'yyyy-MM-dd', 'en-US');
              const currentDate = formatDate(new Date(), 'yyyy-MM-dd', 'en-US');

              // show the banner if the effective is future date
              this.showBanner = effectiveDate > currentDate ? true : false;
            } else if (policySummary.policyTypeCode === 'FamCarPolicy') {
              const effectiveDate = formatDate(policySummary.effectiveDate, 'yyyy-MM-dd', 'en-US');
              const currentDate = formatDate(new Date(), 'yyyy-MM-dd', 'en-US');

              const effectiveDatePlus3Days = new Date(effectiveDate);
              effectiveDatePlus3Days.setDate(effectiveDatePlus3Days.getDate() + 3);
              const effectiveDatePlus3DaysFormatted = formatDate(
                effectiveDatePlus3Days,
                'yyyy-MM-dd',
                'en-US'
              );
              // show the banner if the effective is future date
              this.showBanner = effectiveDatePlus3DaysFormatted > currentDate ? true : false;
            }
          });
        }
      });
    this.overviewService.loadUpsellsWhenNoCrossels();
    this.dismissBanner = this.cookiesService.hasItem('parnter_overview_banner');
  }

  public getPolicyDocument() {
    this.isDocumentCall = true;
    this.store
      .select(PolicySelectors.selectPolicies)
      .pipe(take(1))
      .subscribe(policies => {
        if (policies && policies.length > 0) {
          for (let i = 0; i < policies.length; i++) {
            this.policyDocumentService
              .getDocumentList(
                policies[i].policyNumber,
                policies[i].sourceSystem,
                policies[i].generalizedProductType,
                policies[i].sourcePath
              )
              .pipe(take(1))
              .subscribe(data => {
                data.forEach(document => {
                  if (document.metadata['Document Title'] === 'Policy Declaration Package') {
                    if (!this.documentDate || this.documentDate < document.documentDate) {
                      this.documentDate = document.documentDate;
                      this.documentLink = document.links[0].href;
                      this.policyNumber = document.policyNumber;
                    }
                  }
                });
              });
          }
        }
      });
  }

  public viewDocument() {
    // click analytic for Banner KYD: Learn More Button
    this.analyticsFacade.trackButtonClick(OverviewAnalytics.clickBannerKYDLearnMore);

    let windowRef;
    windowRef = this.win.nativeWindow.open();
    this.documentPipeService
      .retrieveDocument(this.documentLink)
      .pipe(take(1))
      .subscribe(res => {
        if (!res) {
          if (windowRef) {
            windowRef.close();
          }
        }

        if (res) {
          const url = this.sanitizer.sanitize(SecurityContext.URL, res);
          windowRef.location.href = url;
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/policies/auto/' + this.policyNumber],
              query: { openAccordion: 'documents' }
            })
          );
        }
      });
  }

  ngAfterViewInit() {
    const resetPasswordUrl = '/forgot-password/reset-password';
    combineLatest([
      this.store.select(userQuery.selectUserState),
      this.store.select(routerQuery.getRouterPreviousState),
      this.store.select(policiesQuery.selectAllRawPolicies)
    ])
      .pipe(
        filter(
          ([user, previousRoute, policies]) =>
            !!user?.party?.addresses?.length && policies.length > 0
        ),
        take(1)
      )
      .subscribe(([user, previousRoute, policies]) => {
        if (user && user.party.addresses?.length > 0 && policies.length) {
          const userType: string = user.typeOfAccountCode === AccountTypeEnum.CFR ? 'Business' : '';
          const pageAnalytic: PageAnalyticParams = {
            pageName: 'MyAccount:Overview',
            experience: '',
            primaryCategory: 'My Account',
            subCategory1: 'Overview',
            subCategory2: userType,
            subCategory3: ''
          };
          if (_get(previousRoute, 'url') === resetPasswordUrl) {
            this.analyticsFacade.trackPageAndEvent(
              pageAnalytic,
              ForgotPasswordAnalytics.eventResetPasswordComplete
            );
          } else {
            this.store.dispatch(
              AnalyticsActions.storeGtmDetails({
                payload: {
                  user_id: user.trackId,
                  logged_in: '1'
                }
              })
            );
            this.analyticsFacade.trackPage(pageAnalytic);
          }
        }
      });
  }

  closeAlert() {
    this.showAlert = false;
    localStorage.removeItem('vehicleName');
  }

  routeToProfile() {
    this.analyticsFacade.trackButtonClick(
      OverviewAnalytics.overviewMultipleEmailErrorLearnMoreHere
    );
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/profile'],
        query: { openAccordion: 'contact' }
      })
    );
  }

  getQuote(serviceLine: string, category: string) {
    this.store
      .select(userQuery.selectZipCode)
      .pipe(
        withLatestFrom(this.store.select(userQuery.selectUserStateCode)),
        switchMap(([zipCode, stateCode]) => {
          this.setRentersCookie();
          let dspUrl = `https://getquote-bundle-int01.amfam.com/quote/intro?state=${stateCode}&zipCode=${zipCode}&servLineCat=${category}&tid=CrossSellAutoDSPWeb&CrossSellValue=true`;
          if (serviceLine) dspUrl += `&servLine=${serviceLine}`;
          this.coreService.dspUrl = dspUrl;
          this.windowRef.nativeWindow.open(dspUrl, '_blank');
          return of();
        }),
        first(),
        catchError(e => {
          this.router.navigate(['opportunities', 'learnmore', 'auto', 'agent']);
          return of(null);
        })
      )
      .subscribe();
  }

  setRentersCookie() {
    const rentersQualifiers = {
      maritalStatus: 'MARRIED',
      residenceType: 'SingleFamily',
      runBusiness: false,
      ownItems: false,
      occupantsCount: 1
    };
    if (this.cookiesService.hasItem('MYACCOUNT_DETAILS'))
      this.cookiesService.removeItem('MYACCOUNT_DETAILS');
    this.cookiesService.setItem(
      'MYACCOUNT_DETAILS',
      JSON.stringify(rentersQualifiers),
      null,
      '/',
      '.amfam.com'
    );
  }
  clickedOnCancel() {
    this.dismissBanner = true;
    this.cookiesService.setItem('parnter_overview_banner', '1');
  }
}
